/**
 * Con qué pantalla arranca el launcher, a partir de `?vista=` (ADR-007).
 *
 * Vive fuera de `App.tsx` por dos razones: no depende de React, y es la única pieza del launcher
 * con un caso límite que merece una prueba —el viaje al IdP—.
 *
 * Quien llega desde un módulo casi nunca tiene todavía token en memoria, así que `auth-web` se
 * lleva la página al IdP y vuelve a `redirectUri`, que es el origen a secas **sin query**
 * (`docs/FASE-7.md` §9.1: una redirección en cada arranque). Por eso la intención se guarda en
 * `sessionStorage` en vez de leerse y ya: sin ese guardado, el enlace profundo solo funcionaría en
 * el caso raro de que ya hubiera sesión cargada, que es justo el que nadie prueba.
 */

export type Vista = "inicio" | "usuarios" | "cuenta";

/** `inicio` no es enlazable: es el destino por defecto, y guardarlo solo pisaría otra intención. */
const VISTAS_ENLAZABLES = new Set<string>(["usuarios", "cuenta"]);

export const CLAVE_VISTA = "continnua:launcher:vista";

/** Lo que `vistaInicial` necesita del navegador. Inyectable para poder probarla sin uno. */
export interface EntornoVista {
  /** La query de la URL actual, con o sin `?`. */
  busqueda: string;
  /** `sessionStorage`, o algo con su forma. Puede faltar o lanzar. */
  almacen: Pick<Storage, "getItem" | "setItem"> | undefined;
}

function entornoDelNavegador(): EntornoVista {
  return {
    busqueda: globalThis.location?.search ?? "",
    almacen: globalThis.sessionStorage as Storage | undefined,
  };
}

export function vistaInicial(entorno: EntornoVista = entornoDelNavegador()): Vista {
  try {
    const pedida = new URLSearchParams(entorno.busqueda).get("vista");
    if (pedida !== null && VISTAS_ENLAZABLES.has(pedida)) {
      entorno.almacen?.setItem(CLAVE_VISTA, pedida);
      return pedida as Vista;
    }

    const guardada = entorno.almacen?.getItem(CLAVE_VISTA);
    if (guardada !== null && guardada !== undefined && VISTAS_ENLAZABLES.has(guardada)) {
      return guardada as Vista;
    }
  } catch {
    // `sessionStorage` lanza con las cookies bloqueadas y en algunos modos privados, y
    // `URLSearchParams` nunca debería, pero el coste de cubrirlo es cero. El enlace profundo se
    // degrada a la pantalla de inicio, que no es un error: el usuario ya está dentro y la pestaña
    // que buscaba está a un clic.
  }
  return "inicio";
}

/**
 * Consume la intención guardada. Se llama al quedar la sesión autenticada: si se dejara, la
 * siguiente visita a la raíz en esta misma pestaña volvería a abrir «Usuarios» sin que nadie lo
 * haya pedido.
 */
export function olvidarVistaGuardada(almacen: Pick<Storage, "removeItem"> | undefined = globalThis.sessionStorage): void {
  try {
    almacen?.removeItem(CLAVE_VISTA);
  } catch {
    // Ver `vistaInicial`: que no se pueda limpiar no rompe nada.
  }
}
